import {Component} from '@angular/core';
import {NEVER, Observable, of, throwError} from 'rxjs';
import {ProductCardAnalyticsReport, ShineAiReportsService} from '../../../services/ai/shine-ai-reports.service';
import {ChatbotsService} from '../../../services/objects/chatbots.service';
import {AssistantsService} from '../../../services/objects/assistants.service';
import {ProductCardAnalyticsComponent} from './product-card-analytics.component';

class ProductCardAnalyticsVisualFixtureReportsService {
  getProductCardAnalytics(): Observable<ProductCardAnalyticsReport> {
    const state = new URL(window.location.href).searchParams.get('productCardAnalyticsVisualFixture') || 'populated';
    if (state === 'loading') {
      return NEVER;
    }
    if (state === 'error') {
      return throwError(() => new Error('Fixture report error'));
    }

    const report = productCardAnalyticsFixtureReport();
    if (state === 'empty') {
      return of({
        ...report,
        totals: zeroMetrics(),
        trend: [],
        chatbots: [],
        assistants: [],
        productCardTypes: [],
        presentations: [],
        products: []
      });
    }

    return of(report);
  }
}

class ProductCardAnalyticsVisualFixtureChatbotsService {
  getAllAsync() {
    return of([
      {id: 'retail-chatbot-primary-with-long-id-2026', name: 'Nordic Retail Bot'},
      {id: 'support-chatbot', name: 'Support Bot'}
    ]);
  }
}

class ProductCardAnalyticsVisualFixtureAssistantsService {
  getAllAsync() {
    return of([
      {id: 'assistant-product-recommendations-nordics', assistantId: 'asst-product-advisor', name: 'Product Advisor'},
      {id: 'assistant-support-routing', assistantId: 'asst-support-routing', name: 'Support Routing'}
    ]);
  }
}

@Component({
  selector: 'app-product-card-analytics-visual-fixture',
  standalone: true,
  imports: [ProductCardAnalyticsComponent],
  providers: [
    {provide: ShineAiReportsService, useClass: ProductCardAnalyticsVisualFixtureReportsService},
    {provide: ChatbotsService, useClass: ProductCardAnalyticsVisualFixtureChatbotsService},
    {provide: AssistantsService, useClass: ProductCardAnalyticsVisualFixtureAssistantsService}
  ],
  template: '<app-product-card-analytics></app-product-card-analytics>'
})
export class ProductCardAnalyticsVisualFixtureComponent {}

function productCardAnalyticsFixtureReport(): ProductCardAnalyticsReport {
  return {
    startDate: '2026-06-01',
    endDate: '2026-06-18',
    grain: 'Day',
    filters: {
      chatbotId: null,
      assistantId: null,
      productCardType: null,
      presentation: null,
      productSearchHash: null,
      eventSource: null
    },
    totals: {
      displayed: 3280,
      viewed: 1844,
      hovered: 932,
      clicks: 386,
      viewRate: 56.2,
      clickThroughRateByDisplay: 11.8,
      clickThroughRateByView: 20.9,
      uniqueProducts: 128,
      productCardConversations: 417
    },
    rateDefinitions: {
      viewRate: 'viewed / displayed * 100',
      clickThroughRateByDisplay: 'clicks / displayed * 100',
      clickThroughRateByView: 'clicks / viewed * 100'
    },
    trend: [
      trendPoint('2026-06-01', 420, 210, 90, 36),
      trendPoint('2026-06-02', 510, 304, 148, 64),
      trendPoint('2026-06-03', 460, 261, 121, 48),
      trendPoint('2026-06-04', 590, 352, 190, 76),
      trendPoint('2026-06-05', 620, 368, 202, 82),
      trendPoint('2026-06-06', 680, 349, 181, 80)
    ],
    chatbots: [
      breakdown('retail-chatbot-primary-with-long-id-2026', 1860, 1088, 232, 'Nordic Retail Bot'),
      breakdown('support-chatbot', 980, 516, 104),
      breakdown(null, 440, 240, 50)
    ],
    assistants: [
      breakdown('assistant-product-recommendations-nordics', 1720, 990, 208, 'Product Advisor'),
      breakdown('assistant-support-routing', 1120, 614, 138),
      breakdown(null, 440, 240, 40)
    ],
    productCardTypes: [
      breakdown('product', 2200, 1304, 290),
      breakdown('recommendation', 840, 426, 82),
      breakdown('unknown', 240, 114, 14)
    ],
    presentations: [
      breakdown('compact-carousel', 1480, 884, 202),
      breakdown('vertical-carousel', 1260, 704, 132),
      breakdown('horizontal', 540, 256, 52)
    ],
    products: [
      product('product-key-alpha-hash-with-long-identity-value', 'sku-alpha-hash-1234567890abcdef1234567890abcdef', 'name-alpha-hash-1234567890abcdef1234567890abcdef', 420, 290, 126, 88, 'SKU-ALPHA-001', 'Alma Wool Jacket'),
      product('product-key-beta-hash-with-long-identity-value', 'sku-beta-hash-1234567890abcdef1234567890abcdef', 'name-beta-hash-1234567890abcdef1234567890abcdef', 360, 240, 104, 64, 'SKU-BETA-002', 'Embla Shell Pants'),
      product('product-key-gamma-hash-with-long-identity-value', null, 'name-gamma-hash-1234567890abcdef1234567890abcdef', 310, 170, 86, 42),
      product('product-key-delta-hash-with-long-identity-value', 'sku-delta-hash-1234567890abcdef1234567890abcdef', null, 220, 90, 48, 18)
    ]
  };
}

function trendPoint(period: string, displayed: number, viewed: number, hovered: number, clicks: number) {
  return {
    periodStart: period,
    periodLabel: period,
    metrics: metrics(displayed, viewed, hovered, clicks, 24)
  };
}

function breakdown(key: string | null, displayed: number, viewed: number, clicks: number, label?: string) {
  return {
    key,
    label: label ?? null,
    metrics: metrics(displayed, viewed, Math.round(viewed * 0.52), clicks, 16)
  };
}

function product(
  productKey: string,
  skuHash: string | null,
  productNameHash: string | null,
  displayed: number,
  viewed: number,
  hovered: number,
  clicks: number,
  sku?: string,
  productName?: string
) {
  return {
    productKey,
    productCardId: `card-${productKey}`,
    productName: productName ?? null,
    productNameHash,
    sku: sku ?? null,
    skuHash,
    productUrlHash: null,
    metrics: metrics(displayed, viewed, hovered, clicks, 1)
  };
}

function metrics(displayed: number, viewed: number, hovered: number, clicks: number, uniqueProducts: number) {
  return {
    displayed,
    viewed,
    hovered,
    clicks,
    viewRate: rate(viewed, displayed),
    clickThroughRateByDisplay: rate(clicks, displayed),
    clickThroughRateByView: rate(clicks, viewed),
    uniqueProducts,
    productCardConversations: Math.max(1, Math.round(viewed / 3))
  };
}

function zeroMetrics() {
  return metrics(0, 0, 0, 0, 0);
}

function rate(numerator: number, denominator: number): number {
  return denominator > 0 ? Math.round((numerator / denominator) * 1000) / 10 : 0;
}
